import React, { useEffect, useState, Fragment, useCallback } from "react";
import { getAllPackages } from "../../firebase/queries";
import {
  Button,
  Col,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Modal,
  message,
  Spin,
  Switch,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import HeadingComponent from "../../components/HeadingComponent";
import { Package, Packages_Form } from "../../utils/constants";
import { addPackage, deletePackage, updatePackage } from "./APIs/packagesApi";
import CustomTable from "../../components/CustomTable";

const { confirm } = Modal;

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editPackage, setEditPackage] = useState(null);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },

    {
      title: "Product Id",
      dataIndex: "product_id",
    },

    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <EditOutlined
            className="icons_style"
            onClick={() => handleEdit(record)}
          />
          <DeleteOutlined
            className="icons_style"
            onClick={() => showDeleteConfirm(record)}
          />
        </Space>
      ),
    },
  ];
  const showDeleteConfirm = ({ id }) => {
    confirm({
      title: `Are you sure delete this package?`,
      icon: <DeleteOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        handleDelete(id);
      },
    });
  };
  const handleEdit = (record) => {
    setEditPackage(record);
    setIsEdit(true);
  };
  const handleDelete = async (id) => {
    setLoading(true);
    await deletePackage(id).finally(() => {
      setLoading(false);
    });
    const filtered = packages.filter((x) => x.id !== id);
    setPackages(filtered);
    message.success("Package deleted!");
  };

  const getPackages = async () => {
    try {
      setLoading(true);
      let response = await getAllPackages();
      setPackages(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getPackages();
  }, []);

  const setPackagesCallback = useCallback((data) => {
    setPackages((prevState) => {
      if (prevState.find((row) => row.id === data.id)) {
        const index = prevState.findIndex((row) => row.id === data.id);
        prevState.splice(index, 1, data);
      } else {
        prevState.push(data);
      }
      return [...prevState];
    });
  }, []);

  return (
    <div>
      <div className="justify_between">
        <HeadingComponent text="Packages" />
        <AddEditForm
          setPackages={setPackagesCallback}
          isEdit={isEdit}
          editPackage={editPackage}
          setIsEdit={setIsEdit}
        />
      </div>
      <br />

      <CustomTable dataSource={packages} columns={columns} loading={loading} />
    </div>
  );
};

export default Packages;

const AddEditForm = ({
  isEdit = false,
  setIsEdit,
  setPackages,
  editPackage,
}) => {
  const [open, setOpen] = useState(isEdit);
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (isEdit && editPackage) {
      const safeValues = {
        name: editPackage.name || "Default Name",
        product_id: editPackage.product_id || "Default Product ID",
        slug: editPackage.slug || "Default Slug",
        monthlyCostOfYearlyPlan: editPackage.monthlyCostOfYearlyPlan || 0,
        monthlyCostOfMonthlyPlan: editPackage.monthlyCostOfMonthlyPlan || 0,
        oneTimeProductCost: editPackage.oneTimeProductCost || 0,
        history_days: editPackage.history_days || 0,
        short_description: editPackage.short_description || "",
        user_limit: editPackage.user_limit || 2,
        remove_branding: editPackage.remove_branding ?? false,
      };
      form.setFieldsValue(safeValues);
      setOpen(true);
    }
  }, [isEdit, editPackage, form]);

  const showDrawer = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    form.resetFields();
  };

  const onSubmit = () => {
    form.validateFields().then((data) => {
      isEdit ? handleUpdate(data) : handleAdd(data);
    });
  };

  const handleAdd = async (data) => {
    const obj = {
      ...data,
    };
    setFormLoading(true);
    const response = await addPackage(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newPackage = { ...obj, id: response };
      setPackages(newPackage);
      message.success("Package added");
      handleClose();
    }
  };

  const handleUpdate = async (data) => {
    const obj = {
      ...data,
      id: editPackage?.id,
    };
    setFormLoading(true);
    const response = await updatePackage(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newPackage = { ...obj, id: response };
      setPackages(newPackage);
      message.success("Package Updated");
      handleClose();
    }
  };

  const handleNameChange = (e) => {
    const packageName = e.target.value;
    const slug = packageName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    form.setFieldsValue({ [Packages_Form.slug.name]: slug });
  };

  return (
    <Fragment>
      <Button
        type="primary"
        onClick={showDrawer}
        icon={<PlusOutlined />}
        size="medium"
      >
        Add Package
      </Button>

      <Drawer
        title={isEdit ? `Edit ${Package}` : `Add ${Package}`}
        width={720}
        onClose={handleClose}
        open={open}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <Spin spinning={formLoading}>
          <Form layout="vertical" form={form} onFinish={onSubmit}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.package_name.name}
                  label={Packages_Form.package_name.label}
                  rules={Packages_Form.package_name.rules}
                >
                  <Input
                    placeholder={Packages_Form.package_name.placeholder}
                    onChange={handleNameChange}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.slug.name}
                  label={Packages_Form.slug.label}
                  rules={Packages_Form.slug.rules}
                >
                  <Input
                    disabled
                    placeholder={Packages_Form.slug.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.monthlyCostOfYearlyPlan.name}
                  label={Packages_Form.monthlyCostOfYearlyPlan.label}
                  rules={Packages_Form.monthlyCostOfYearlyPlan.rules}
                >
                  <InputNumber
                    className="full_width"
                    placeholder={
                      Packages_Form.monthlyCostOfYearlyPlan.placeholder
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.monthlyCostOfMonthlyPlan.name}
                  label={Packages_Form.monthlyCostOfMonthlyPlan.label}
                  rules={Packages_Form.monthlyCostOfMonthlyPlan.rules}
                >
                  <InputNumber
                    className="full_width"
                    placeholder={
                      Packages_Form.monthlyCostOfMonthlyPlan.placeholder
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.oneTimeProductCost.name}
                  label={Packages_Form.oneTimeProductCost.label}
                  rules={Packages_Form.oneTimeProductCost.rules}
                >
                  <Input
                    placeholder={Packages_Form.oneTimeProductCost.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.product_id.name}
                  label={Packages_Form.product_id.label}
                  rules={Packages_Form.product_id.rules}
                >
                  <Input placeholder={Packages_Form.product_id.placeholder} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.short_description.name}
                  label={Packages_Form.short_description.label}
                >
                  <Input
                    maxLength={20}
                    placeholder={Packages_Form.short_description.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.user_limit.name}
                  label={Packages_Form.user_limit.label}
                >
                  <InputNumber
                    className="full_width"
                    min={1}
                    placeholder={Packages_Form.user_limit.placeholder}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.history_days.name}
                  label={Packages_Form.history_days.label}
                  rules={Packages_Form.history_days.rules}
                >
                  <InputNumber
                    className="full_width"
                    placeholder={Packages_Form.history_days.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Form.remove_branding.name}
                  label={Packages_Form.remove_branding.label}
                >
                  <Switch
                    defaultChecked={false}
                    placeholder={Packages_Form.remove_branding.placeholder}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Flex justify="flex-end">
              <Space>
                <Button onClick={handleClose}>Cancel</Button>
                <Button htmlType="submit" type="primary">
                  Submit
                </Button>
              </Space>
            </Flex>
          </Form>
        </Spin>
      </Drawer>
    </Fragment>
  );
};
